/**
 * Mobile-only "next" arrow for horizontally scrollable product carousels.
 * Scrolls exactly one card width per tap and disables itself once the
 * last card is in view. Purely additive — swipe/scroll-snap keeps working
 * untouched, and the button is CSS-hidden above the mobile breakpoint so
 * desktop/tablet layouts are unaffected.
 *
 * Markup contract: a [data-flv-carousel] wrapper (position: relative)
 * containing a [data-flv-carousel-track] scroll container and a
 * [data-flv-carousel-next] button. Optional data-flv-carousel-card
 * selector identifies the scrollable unit when it isn't the track's
 * direct child (e.g. a slide wrapper around the product card).
 */
function initCarouselArrow(root) {
  const track = root.querySelector('[data-flv-carousel-track]');
  const button = root.querySelector('[data-flv-carousel-next]');
  if (!(track instanceof HTMLElement) || !(button instanceof HTMLButtonElement)) return;

  const cardSelector = root.dataset.flvCarouselCard;

  const cardStep = () => {
    const card = cardSelector ? track.querySelector(cardSelector) : track.firstElementChild;
    const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
    return card instanceof HTMLElement ? card.getBoundingClientRect().width + gap : track.clientWidth;
  };

  const updateState = () => {
    const maxScroll = track.scrollWidth - track.clientWidth;
    button.disabled = maxScroll <= 1 || track.scrollLeft >= maxScroll - 1;
  };

  button.addEventListener('click', () => {
    track.scrollBy({ left: cardStep(), behavior: 'smooth' });
  });

  track.addEventListener('scroll', () => window.requestAnimationFrame(updateState), { passive: true });
  window.addEventListener('resize', updateState);
  // Related-products carousels fetch their cards after this script has
  // already initialized against an empty track — re-check once content lands.
  new MutationObserver(updateState).observe(track, { childList: true });

  updateState();
}

document.querySelectorAll('[data-flv-carousel]').forEach(initCarouselArrow);
